import {makeAutoObservable} from "mobx";
import CsInstance from "./CsInstance";
import type {
	ActorList,
	ActorType,
	UserActorList,
	TheaterState,
} from "../@types/Types";

/**
 * MobX store for multiplayer state.
 */
class MultiplayerStore {
	private _csInstance: CsInstance;
	private _isMultiplayer = false;
	private _showMultiplayer = false;

	// Determined by activity yaml
	private _trackedClass = "";

	// After the user has selected an actor to track, this is the uuid of the actor
	private _trackedUuid = "";

	// The user's full actor state
	private _myActorState: ActorType = {} as ActorType;

	// The user's actor states at each frame
	private _myActorFrames: ActorList = [];

	// The other user's actor states at each frame
	// key is the user's uuid
	private _otherActorFrames: UserActorList = {};

	// A set of all the multiplayer actors that have been added to the stage
	// Needed to delete them when turning off multiplayer mode
	private multiplayeruuids: Set<string> = new Set([]);

	private _sendStates = (states: ActorList) => {};

	// Interval for sending the user's actor states to the server
	private _multiplayerInterval: NodeJS.Timeout | null = null;

	// Interval for sending other user's actor state to Pixi
	private _updateTheaterInterval: NodeJS.Timeout | null = null;

	constructor(props: {
		csInstance: CsInstance;
		sendStates?: (states: ActorList) => void;
	}) {
		this._sendStates = props.sendStates || this._sendStates;
		this._csInstance = props.csInstance;
		makeAutoObservable(this);
	}

	/**
	 * Starts the interval for sending the user's actor states to the server
	 * and the interval for sending other user's actor state to worker for Pixi
	 */
	startMultiplayerInterval() {
		if (this._multiplayerInterval) {
			clearInterval(this._multiplayerInterval);
		}

		this._multiplayerInterval = setInterval(() => {
			if (this._myActorFrames.length > 0) {
				this._sendStates(this._myActorFrames);
			} else {
				this._sendStates([this._myActorState ?? ({} as ActorType)]);
			}
			this._myActorFrames = [];
		}, 1000);

		if (this._updateTheaterInterval) {
			clearInterval(this._updateTheaterInterval);
		}

		this._updateTheaterInterval = setInterval(() => {
			const toSend: ActorList = [];
			Object.values(this._otherActorFrames).forEach((actorList) => {
				if (actorList.length > 0) {
					const toPush = actorList.shift();
					if (toPush) {
						toSend.push(toPush);
						toPush.uuid && this.multiplayeruuids.add(toPush.uuid);
					}
				}
			});
			toSend.push({uuid: this._trackedUuid, border: true} as ActorType);
			if (toSend.length > 0 && this._showMultiplayer) {
				this._csInstance
					.getOrCreateMessenger()
					.updateMultiplayerTheater(JSON.stringify(toSend));
			}
		}, 16);
	}

	/**
	 * Stops the interval for sending the user's actor states to the server
	 * and the interval for sending other user's actor state to worker for Pixi
	 */
	stopMultiplayerInterval() {
		if (this._multiplayerInterval) {
			clearInterval(this._multiplayerInterval);
			this._multiplayerInterval = null;
		}
		if (this._updateTheaterInterval) {
			clearInterval(this._updateTheaterInterval);
			this._updateTheaterInterval = null;
		}
	}

	// On reset, send a delete signal to server
	resetTrackedUuid() {
		if (this._myActorState) {
			const stateToSend = {...this._myActorState, removed: true};
			this._myActorFrames.push(stateToSend);
		} else if (this._trackedUuid) {
			this._myActorFrames.push({
				uuid: this._trackedUuid,
				removed: true,
			} as ActorType);
		}
		this._trackedUuid = "";
	}

	// Need to a have a separate setter since the value of setMyFrames is an object
	// and we need to parse it from a string
	// Gets called when receiving updates from CheerpJ JVM
	setMyActorFrames(value: string) {
		try {
			const parsed = JSON.parse(value) as TheaterState;
			if (!parsed.actors) {
				return;
			}

			parsed.actors.forEach((actor: ActorType) => {
				// If the user is not tracking an actor, and the actor is the one they want to track, set the trackedUuid to the actor's uuid
				if (!this._trackedUuid && actor.class === this._trackedClass) {
					this._trackedUuid = actor.uuid;
				}
				// If the user is tracking an actor, and the actor is the one they are tracking, update the user's state
				if (this._trackedUuid && actor.uuid === this._trackedUuid) {
					if (!this._myActorState) {
						this._myActorState = actor;
					} else {
						// Need to merge the actor state because the actor's state can be a subset of the user's state
						this._myActorState = {
							...this._myActorState,
							...actor,
						};
					}
					this._myActorFrames.push(this._myActorState as ActorType);
					return;
				}
			});
		} catch (e) {
			console.error("Failed to parse myActorFrames", {e, value});
		}
	}

	// Called when receiving updates from CheerpJ JVM
	// Updates the otherFrames object with the new states
	updateOtherActorFrames(updateJson: UserActorList) {
		for (const [uuid, actorFrames] of Object.entries(updateJson)) {
			if (
				uuid in this._otherActorFrames &&
				this._otherActorFrames[uuid] !== undefined
			) {
				for (const actorFrame of Object.entries(actorFrames)) {
					this._otherActorFrames[uuid]!.push(actorFrame[1]);
				}
			} else {
				this._otherActorFrames[uuid] = [];
				for (const actorFrame of Object.entries(actorFrames)) {
					this._otherActorFrames[uuid]!.push(actorFrame[1]);
				}
			}
		}
	}

	// Called when turning off multiplayer mode
	// Removes all multiplayer non user actors from the stage
	removeMultiplayerActors() {
		const toSend: ActorList = [];
		for (const uuid of this.multiplayeruuids) {
			toSend.push({
				uuid,
				removed: true,
			} as ActorType);
			this.multiplayeruuids.delete(uuid);
		}
		if (this._trackedUuid) {
			toSend.push({uuid: this._trackedUuid, border: false} as ActorType);
		}
		this._csInstance
			.getOrCreateMessenger()
			.updateMultiplayerTheater(JSON.stringify(toSend));
	}

	// Getters and Setters
	get isMultiplayer() {
		return this._isMultiplayer;
	}
	set isMultiplayer(value) {
		this._isMultiplayer = value;
	}

	get trackedClass() {
		return this._trackedClass;
	}
	set trackedClass(value) {
		this._trackedClass = value;
	}

	get myActorFrames() {
		return this._myActorFrames;
	}
	set myActorFrames(value) {
		this._myActorFrames = value;
	}

	get otherActorFrames() {
		return this._otherActorFrames;
	}
	set otherActorFrames(value) {
		if (!this._isMultiplayer) {
			this._otherActorFrames = value;
		}
	}

	get sendStates() {
		return this._sendStates;
	}
	set sendStates(value) {
		this._sendStates = value;
	}

	get showMultiplayer() {
		return this._showMultiplayer;
	}
	set showMultiplayer(value) {
		this._showMultiplayer = value;
		if (!value) {
			this.removeMultiplayerActors();
		}
	}
}

export default MultiplayerStore;
